import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/cn';
import { flavorClass, type Flavor } from './flavors';

/** A bouncing 3-dot pulse in the app's flavor palette — the on-theme stand-in
 * for a generic spinner, wherever "loading" deserves a little more life:
 * toasts, page loaders, buttons mid-request. Freezes to a soft blink under
 * `prefers-reduced-motion`. */
export function PulseLoader({
  flavor = 'lychee',
  size = 'md',
  className,
}: {
  flavor?: Flavor;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const reduce = useReducedMotion();
  const dot = size === 'sm' ? 'size-1.5' : 'size-2.5';
  return (
    <div
      className={cn('inline-flex items-center gap-1.5', className)}
      role="status"
      aria-label="Loading"
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className={cn('rounded-full', dot, flavorClass[flavor].bg)}
          animate={reduce ? { opacity: 0.6 } : { y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}
